/* import __COLOCATED_TEMPLATE__ from './company-profile-cost-codes.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { inject as service } from '@ember/service';
import { all, task } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-company-profile-cost-codes';

const SECTIONS_DICT = {
  COST_CODES: 'costCodes',
  STORY_CODES: 'storyCodes',
  INACTIVE_COST_CODES: 'inactiveCostCodes',
  INACTIVE_STORY_CODES: 'inactiveStoryCodes',
};

class CostCodes extends Component {
  @service('commands/company-commands') companyCommandService;
  @service('commands.cost-code-commands') costCodeCommandService;
  @service('commands.story-code-commands') storyCodeCommandService;
  @service('commands.user-commands') userCommandService;
  @service errorTracker;
  @service session;

  @tracked allCostCodes = [];
  @tracked allStoryCodes = [];

  get classNames() {
    return clsx(
      CLASS_NAME,
      this.args.class,
    );
  }

  get userIsAdmin() {
    const { currentUser } = this.session;
    return currentUser.isCompanyAdmin || currentUser.isAdmin;
  }

  get companyTeams() {
    return this.loadCompany.lastSuccessful?.value.companyTeams ?? [];
  }

  get usingCompanyTeams() {
    return this.args.company.canUseCompanyTeams && this.companyTeams.length;
  }

  // Admins can use all company teams
  get userCompanyTeams() {
    if (this.userIsAdmin) {
      return this.companyTeams;
    }

    return this.loadUserCompanyTeams.lastSuccessful?.value.companyTeams ?? [];
  }

  #sortedCodesCache = createCache(() => {
    const result = {
      [SECTIONS_DICT.COST_CODES]: [],
      [SECTIONS_DICT.STORY_CODES]: [],
      [SECTIONS_DICT.INACTIVE_COST_CODES]: [],
      [SECTIONS_DICT.INACTIVE_STORY_CODES]: [],
    }

    // Sort all cost codes by section
    this.allCostCodes.forEach((code) => {
      if (!code.deactivated) {
        result[SECTIONS_DICT.COST_CODES].push(code);
      } else {
        result[SECTIONS_DICT.INACTIVE_COST_CODES].push(code);
      }
    });

    // Sort all  storycodes by section
    this.allStoryCodes.forEach((code) => {
      if (!code.deactivated) {
        result[SECTIONS_DICT.STORY_CODES].push(code);
      } else {
        result[SECTIONS_DICT.INACTIVE_STORY_CODES].push(code);
      }
    });

    // Sort each section alphabetically
    Object.keys(result).forEach(key => {
      result[key].sort((a, b) => a.body.localeCompare(b.body))
    });

    return result;
  });

  get sortedCodes() {
    return getValue(this.#sortedCodesCache);
  }

  get costCodes() {
    return this.sortedCodes[SECTIONS_DICT.COST_CODES];
  }

  get storyCodes() {
    return this.sortedCodes[SECTIONS_DICT.STORY_CODES];
  }

  get inactiveCostCodes() {
    return this.sortedCodes[SECTIONS_DICT.INACTIVE_COST_CODES];
  }

  get inactiveStoryCodes() {
    return this.sortedCodes[SECTIONS_DICT.INACTIVE_STORY_CODES];
  }

  @task
  *initializeData() {
    const canUseCompanyTeams = this.args.company.canUseCompanyTeams;
    const pending = [this.loadCompany.perform(canUseCompanyTeams)];

    if (canUseCompanyTeams && !this.userIsAdmin) {
      pending.push(this.loadUserCompanyTeams.perform());
    }

    return yield all(pending);
  }

  @task
  *loadCompany(includeTeams = false) {
    const includes = [
      'cost-codes:include_deactivated(true).company-teams',
      'story-codes:include_deactivated(true).company-teams',
    ];
    if (includeTeams) {
      includes.push('company-teams');
    }

    try {
      const response = yield this.companyCommandService.loadCompany(this.args.company.id, includes);
      this.allCostCodes = response.costCodes.toArray();
      this.allStoryCodes = response.storyCodes.toArray();
      return response;
    } catch (error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading company codes. Please try again or contact support if the problem continues.",
        `loadCompany -- error loading cost codes for company id: ${this.args.company.id}`,
        "Error loading the tab"
      );
    }
  }

  @task
  *loadUserCompanyTeams() {
    const { currentUser } = this.session;
    return yield this.userCommandService.getUser(currentUser.id, ['company-teams']);
  }

  @task
  *addNewCostCode(body) {
    try {
      const newCode =  yield this.costCodeCommandService.createCostCode(this.args.company, body);
      newCode.company = this.args.company;
      this.allCostCodes = [newCode, ...this.allCostCodes];
    } catch (error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem creating your new code. Please try again or contact support if the problem continues.",
        `addNewCode -- error creating cost code for company id: ${this.args.company.id}`,
      );
    }
  }

  @task
  *addNewStoryCode(body) {
    try {
      const newCode =  yield this.storyCodeCommandService.createStoryCode(this.args.company, body);
      newCode.company = this.args.company;
      this.allStoryCodes = [newCode, ...this.allStoryCodes];
    } catch (error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem creating your new code. Please try again or contact support if the problem continues.",
        `addNewCode -- error creating story code for company id: ${this.args.company.id}`,
      );
    }
  }

  @task
  *loadCostCode(codeId) {
    const includes = this.usingCompanyTeams ? ['company-teams'] : [];

    try {
      const reloadedCode =  yield this.costCodeCommandService.getCostCodeById(codeId, includes);
      this.allCostCodes = this.allCostCodes
        .filter(code => code.id !== reloadedCode.id)
        .concat(reloadedCode);
    } catch (error) {
      this.errorTracker.error(`loadCode failed for code id ${codeId}`, this, error);
    }
  }

  @task
  *loadStoryCode(codeId) {
    const includes = this.usingCompanyTeams ? ['company-teams'] : [];

    try {
      const reloadedCode =  yield this.storyCodeCommandService.getStoryCodeById(codeId, includes);
      this.allStoryCodes = this.allStoryCodes
        .filter(code => code.id !== reloadedCode.id)
        .concat(reloadedCode);
    } catch (error) {
      this.errorTracker.error(`loadCode failed for code id ${codeId}`, this, error);
    }
  }
}

export default CostCodes;
